<script>
/**
 * 시세 수수료 관리 — HQ 전용.
 *
 * 값은 공용 서버(common-server)에 있고 이 화면이 관리한다. 거기가 고시가를 계산하는
 * 자리라 정책을 알아야 한다.
 *
 * 세 가지가 이 화면의 전부다.
 *
 *  1. 기본 수수료는 **더해진다.** 양방향에 깔고 방향별 수수료를 그 위에 얹는다.
 *     기본 1% + 살때 2% 는 살 때 3% 다. 대체가 아니다.
 *  2. 칸마다 **퍼센트 또는 금액 중 하나만** 쓴다.
 *  3. 섞이면 **퍼센트를 먼저** 먹이고 금액을 더한다. 정액에 퍼센트가 다시 붙지 않는다.
 *
 * 고치는 것이 아니라 **새 버전을 쌓는다.** 앞 버전은 같은 시각에 닫힌다. 지난달 주문은
 * 그때 값으로 가격이 매겨졌고 "왜 그 가격이었나" 는 계속 답할 수 있어야 한다.
 *
 * 미리보기를 크게 둔 이유 — 2.00 을 200 으로 넣는 사고가 흔한데, 서버가 막아 주기는 해도
 * 저장을 눌러야 알게 된다. 입력하는 동안 고객가가 같이 움직이면 그전에 눈에 띈다.
 */
import {
  getFeePolicies,
  scheduleFeePolicy,
  deactivateFeePolicy,
  getLatestQuotes,
} from '@/api/feePolicy.js'

const TENANTS = [
  { key: 'GOLDNARA', label: '골드나라' },
  { key: 'AURA', label: '아우라' },
]

const METALS = [
  { key: 'GOLD', label: '금', color: '#D4AF37' },
  { key: 'SILVER', label: '은', color: '#9E9E9E' },
  { key: 'PLATINUM', label: '백금', color: '#5C9EAD' },
]

const FEE_TYPES = [
  { title: '퍼센트 (%)', value: 'PCT' },
  { title: '금액 (원/g)', value: 'AMOUNT' },
]

/**
 * 시세를 아직 못 받았을 때 미리보기가 쓸 값(원/g). 2026-10-18 실측이다.
 * 수집이 돌기 시작하면 살아 있는 값이 이걸 대신한다.
 */
const SAMPLE_QUOTE = { GOLD: 178677, SILVER: 2446, PLATINUM: 69770 }

const BASIS_LABEL = { KRX: 'KRX 금현물', SPOT: '해외 현물' }

function blankForm(metal = 'GOLD') {
  return {
    metal,
    baseFeeType: 'PCT',
    baseFeeValue: 0,
    buyFeeType: 'PCT',
    buyFeeValue: 0,
    sellFeeType: 'PCT',
    sellFeeValue: 0,
    roundUnit: 1,
    effectiveFrom: '',
    memo: '',
  }
}

export default {
  name: 'FeePolicyPage',

  data() {
    return {
      TENANTS,
      METALS,
      FEE_TYPES,
      tenant: 'GOLDNARA',
      policies: [],
      quotes: [],
      quotesAt: null,
      form: blankForm(),
      loading: false,
      saving: false,
      errorMessage: '',
      noticeMessage: '',
    }
  },

  computed: {
    /**
     * 지금 이 순간 효력이 있는 버전. 금속마다 하나다.
     *
     * "끝나지 않은 것" 으로 고르면 안 된다 - 미래 시각으로 예약을 걸면 지금 쓰는 버전이
     * 그 시각으로 닫히면서 effectiveTo 가 생긴다. 아직 효력이 있는데도 목록에서 빠져
     * "설정 전" 으로 보인다. 시작·종료를 지금과 견줘서 고른다.
     */
    currentByMetal() {
      const now = Date.now()
      const out = {}
      for (const p of this.policies) {
        if (!p.active || out[p.metal]) continue
        const from = p.effectiveFrom ? new Date(p.effectiveFrom).getTime() : 0
        const to = p.effectiveTo ? new Date(p.effectiveTo).getTime() : Infinity
        if (from <= now && now < to) {
          out[p.metal] = p
        }
      }
      return out
    },

    /** 금속별로 묶은 시세. 금은 두 줄(국내 KRX · 해외 현물)이다. */
    quotesByMetal() {
      const out = {}
      for (const q of this.quotes) {
        ;(out[q.metal] = out[q.metal] || []).push(q)
      }
      // 국내 기준(preferred)을 위로. 서버가 알려 준 값이라 화면이 규칙을 베끼지 않는다.
      for (const k of Object.keys(out)) {
        out[k].sort((a, b) => Number(b.preferred) - Number(a.preferred))
      }
      return out
    },

    /** 고시가의 근거가 되는 줄만. 금은 KRX, 은·백금은 환율로 환산한 해외 현물이다. */
    domesticByMetal() {
      const out = {}
      for (const q of this.quotes) {
        if (q.preferred) out[q.metal] = q
      }
      return out
    },

    /** 미리보기 기준값. 살아 있는 시세가 있으면 그걸 쓰고, 없으면 표본으로 떨어진다. */
    sampleQuote() {
      const live = this.domesticByMetal[this.form.metal]
      return live ? Number(live.krwPerGram) : SAMPLE_QUOTE[this.form.metal] || 0
    },

    /** 미리보기가 지금 무엇을 기준으로 말하고 있는지. 표본이면 그렇다고 적는다. */
    sampleSource() {
      const live = this.domesticByMetal[this.form.metal]
      if (!live) return '표본값, 2026-10-18 실측'
      return `${BASIS_LABEL[live.basis] || live.basis} · ${this.dateText(live.quotedAt)}`
    },

    /**
     * 입력한 수수료로 계산한 고객가. 서버의 CustomerPricer 와 같은 식이다.
     *
     *   살 때 = 시세 x (1 + 기본% + 살때%) + 기본액 + 살때액
     *   팔 때 = 시세 x (1 - 기본% - 팔때%) - 기본액 - 팔때액
     */
    preview() {
      const q = this.sampleQuote
      if (!q) return null

      const pct = (type, v) => (type === 'PCT' ? Number(v || 0) / 100 : 0)
      const amt = (type, v) => (type === 'AMOUNT' ? Number(v || 0) : 0)
      const f = this.form

      const basePct = pct(f.baseFeeType, f.baseFeeValue)
      const baseAmt = amt(f.baseFeeType, f.baseFeeValue)
      const buy = q * (1 + basePct + pct(f.buyFeeType, f.buyFeeValue))
        + baseAmt + amt(f.buyFeeType, f.buyFeeValue)
      const sell = q * (1 - basePct - pct(f.sellFeeType, f.sellFeeValue))
        - baseAmt - amt(f.sellFeeType, f.sellFeeValue)

      // 반올림은 양쪽 다 회사 쪽이다 - 살 때는 올리고 팔 때는 내린다(2026-10-19 지시).
      // 서버 CustomerPricer 의 CEILING/FLOOR 와 같은 규칙이라야 표와 미리보기가 같은
      // 숫자를 낸다. 한 화면에서 둘이 1원 다르면 어느 쪽이 맞는지 알 수 없다.
      const unit = Math.max(1, Number(f.roundUnit) || 1)
      const up = (v) => Math.ceil(v / unit) * unit
      const down = (v) => Math.floor(v / unit) * unit

      return {
        buy: up(buy),
        sell: down(sell),
        spread: up(buy) - down(sell),
        // 팔 때가 0 이하면 수수료가 시세를 다 먹은 것이다. 서버도 거절한다.
        broken: sell <= 0,
      }
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.errorMessage = ''
      try {
        const [polRes, quoteRes] = await Promise.allSettled([
          getFeePolicies(this.tenant),
          getLatestQuotes(this.tenant),
        ])

        if (polRes.status === 'fulfilled' && polRes.value.data.code === 0) {
          this.policies = polRes.value.data.data || []
        } else {
          this.policies = []
          this.errorMessage =
            polRes.status === 'fulfilled'
              ? polRes.value.data.message || '수수료 정책을 불러오지 못했습니다.'
              : polRes.reason?.message || '수수료 정책을 불러오지 못했습니다.'
        }

        // 시세는 없어도 수수료 관리는 되어야 한다. 실패해도 정책 화면을 막지 않는다.
        if (quoteRes.status === 'fulfilled' && quoteRes.value.data.code === 0) {
          const body = quoteRes.value.data.data || {}
          this.quotes = body.items || []
          this.quotesAt = body.servedAt || null
        } else {
          this.quotes = []
          this.quotesAt = null
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message
        this.policies = []
      } finally {
        this.loading = false
      }
    },

    async onTenant(next) {
      this.tenant = next
      this.form = blankForm(this.form.metal)
      await this.load()
    },

    /** 지금 적용 중인 값을 입력칸에 불러온다. 한 항목만 바꿀 때 전부 다시 치지 않도록. */
    copyCurrent(metal) {
      const p = this.currentByMetal[metal]
      if (!p) return
      this.form = {
        ...blankForm(metal),
        baseFeeType: p.baseFeeType,
        baseFeeValue: Number(p.baseFeeValue),
        buyFeeType: p.buyFeeType,
        buyFeeValue: Number(p.buyFeeValue),
        sellFeeType: p.sellFeeType,
        sellFeeValue: Number(p.sellFeeValue),
        roundUnit: p.roundUnit,
      }
      this.noticeMessage = `${this.metalLabel(metal)} 의 현재 값을 불러왔습니다. 바꾼 뒤 저장하면 새 버전이 됩니다.`
    },

    async save() {
      this.saving = true
      this.errorMessage = ''
      this.noticeMessage = ''
      try {
        const body = {
          tenant: this.tenant,
          ...this.form,
          baseFeeValue: Number(this.form.baseFeeValue),
          buyFeeValue: Number(this.form.buyFeeValue),
          sellFeeValue: Number(this.form.sellFeeValue),
          roundUnit: Number(this.form.roundUnit),
          effectiveFrom: this.form.effectiveFrom
            ? new Date(this.form.effectiveFrom).toISOString()
            : null,
        }
        const { data } = await scheduleFeePolicy(body)
        if (data.code !== 0) {
          // 공용 서버가 "팔때 수수료가 시세를 다 먹는다" 같은 말을 돌려준다.
          // 그 문장이 그대로 보여야 무엇을 고쳐야 할지 안다.
          this.errorMessage = data.message || '저장하지 못했습니다.'
          return
        }
        this.noticeMessage = '새 수수료 버전을 등록했습니다. 앞 버전은 같은 시각에 닫혔습니다.'
        this.form = blankForm(this.form.metal)
        await this.load()
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message
      } finally {
        this.saving = false
      }
    },

    async deactivate(p) {
      if (!window.confirm(`${this.metalLabel(p.metal)} 의 이 버전을 끕니다. 기록은 남습니다.`)) {
        return
      }
      this.errorMessage = ''
      try {
        const { data } = await deactivateFeePolicy(p.id)
        if (data.code !== 0) {
          this.errorMessage = data.message || '끄지 못했습니다.'
          return
        }
        await this.load()
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message
      }
    },

    /** 아직 시작 전인 예약. 이력에서 '적용 중' 과 갈라 보여야 한다. */
    scheduled(p) {
      return p.active && p.effectiveFrom && new Date(p.effectiveFrom).getTime() > Date.now()
    },

    metalLabel(key) {
      return (METALS.find((m) => m.key === key) || {}).label || key
    },

    feeText(type, value) {
      const v = Number(value || 0)
      return type === 'PCT' ? `${v}%` : `${v.toLocaleString()}원`
    },

    money(v) {
      return v == null ? '-' : Number(v).toLocaleString()
    },

    /** 원화는 정수로, 달러는 소수 둘째 자리까지. 단위가 다르면 자릿수도 달라야 한다. */
    num(v, digits = 0) {
      if (v == null) return '-'
      return Number(v).toLocaleString('ko-KR', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },

    basisLabel(b) {
      return BASIS_LABEL[b] || b
    },

    dateText(iso) {
      if (!iso) return '-'
      return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })
    },
  },
}
</script>


<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">시세 수수료</div>
        <div class="text-body-2 text-grey mt-1">
          기준 시세에 얹어 고객가를 만든다 — 법인별로 따로 관리한다
        </div>
      </div>
    </div>

    <!-- 기준 안내. 금속마다 기준이 달라서 화면이 먼저 말해 줘야 한다. -->
    <v-alert type="info" variant="tonal" density="comfortable" class="mb-5" border="start">
      <div class="text-body-2">
        <strong>금</strong>은 KRX 금현물(1g, 원화) 기준입니다. 환율을 적용하지 않습니다.
        장 마감 후와 휴장일에는 <strong>그날 마지막 KRX 값으로 고정</strong>되고 다음 개장 때 다시 움직입니다.
      </div>
      <div class="text-body-2 mt-1">
        <strong>은·백금</strong>은 KRX 에 시장이 없어 <strong>해외 현물 시세(USD/트로이온스)</strong>를
        기준으로 하며 USD/KRW 환율로 환산해 원/g 으로 보여 줍니다.
        해외 시세가 멈춘 동안에도 <strong>환율이 바뀌면 원화 금액은 따라 움직입니다.</strong>
      </div>
      <div class="text-body-2 mt-1">
        아래 수수료는 위 기준 시세에 적용되며, <strong>부가세는 포함되어 있지 않습니다.</strong>
      </div>
    </v-alert>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      density="comfortable"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >{{ errorMessage }}</v-alert>

    <v-alert
      v-if="noticeMessage"
      type="success"
      variant="tonal"
      density="comfortable"
      class="mb-4"
      closable
      @click:close="noticeMessage = ''"
    >{{ noticeMessage }}</v-alert>

    <!-- 법인 -->
    <v-tabs v-model="tenant" color="amber-darken-2" class="mb-4" @update:model-value="onTenant">
      <v-tab v-for="t in TENANTS" :key="t.key" :value="t.key">{{ t.label }}</v-tab>
    </v-tabs>

    <!-- 지금 시세. 해외(USD/트로이온스)와 국내(원/g)를 한 표에 둔다 - 금은 기준이
         둘이라 두 줄이고, 어느 줄이 고시가의 근거인지 칩으로 표시한다. -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
        지금 시세
        <span class="text-caption text-grey ml-2">
          기준시세 · 살 때 · 팔 때. 부가세 전 금액입니다
        </span>
        <v-spacer />
        <span v-if="quotesAt" class="text-caption text-grey">{{ dateText(quotesAt) }} 기준</span>
        <v-btn size="small" variant="text" :loading="loading" @click="load">새로고침</v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <v-table density="comfortable">
          <thead>
            <tr>
              <th>금속</th>
              <th>기준</th>
              <th class="text-right">해외 (USD/oz)</th>
              <th class="text-right">환율</th>
              <th class="text-right">기준시세 (원/g)</th>
              <th class="text-right">살 때 (원/g)</th>
              <th class="text-right">팔 때 (원/g)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="quotes.length === 0">
              <td colspan="7" class="text-center text-grey py-6">
                아직 수집된 시세가 없습니다 — 공용 서버 연결과 수집 설정을 확인하세요
              </td>
            </tr>
            <template v-for="m in METALS" :key="m.key">
              <tr v-for="q in (quotesByMetal[m.key] || [])" :key="m.key + q.basis">
                <td>
                  <v-icon :color="m.color" size="12" class="mr-1">mdi-circle</v-icon>{{ m.label }}
                </td>
                <td>
                  {{ basisLabel(q.basis) }}
                  <v-chip v-if="q.preferred" size="x-small" color="green" variant="tonal" class="ml-1">
                    국내 고시 기준
                  </v-chip>
                  <v-chip v-else size="x-small" variant="tonal" class="ml-1">참고</v-chip>
                </td>
                <td class="text-right">
                  <span v-if="q.raw && q.raw.currency === 'USD'">{{ num(q.raw.price, 2) }}</span>
                  <span v-else class="text-grey">-</span>
                </td>
                <td class="text-right">
                  <span v-if="q.fxRate">{{ num(q.fxRate, 2) }}</span>
                  <span v-else class="text-grey">-</span>
                </td>
                <td class="text-right">{{ num(q.krwPerGram) }}</td>
                <td class="text-right">
                  <span v-if="q.priced" class="fee-buy">{{ num(q.customerBuyKrwPerGram) }}</span>
                  <span v-else class="text-grey">수수료 미설정</span>
                </td>
                <td class="text-right">
                  <span v-if="q.priced" class="fee-sell">{{ num(q.customerSellKrwPerGram) }}</span>
                  <span v-else class="text-grey">-</span>
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- 지금 적용 중 -->
      <v-col cols="12" md="5">
        <v-card rounded="lg" elevation="0" border>
          <v-card-title class="text-subtitle-1 font-weight-bold">지금 적용 중</v-card-title>
          <v-divider />
          <v-card-text class="pa-0">
            <v-table density="comfortable">
              <thead>
                <tr>
                  <th>금속</th>
                  <th>기본</th>
                  <th>살 때</th>
                  <th>팔 때</th>
                  <th class="text-right"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in METALS" :key="m.key">
                  <td>
                    <v-icon :color="m.color" size="12" class="mr-1">mdi-circle</v-icon>{{ m.label }}
                  </td>
                  <template v-if="currentByMetal[m.key]">
                    <td>{{ feeText(currentByMetal[m.key].baseFeeType, currentByMetal[m.key].baseFeeValue) }}</td>
                    <td>{{ feeText(currentByMetal[m.key].buyFeeType, currentByMetal[m.key].buyFeeValue) }}</td>
                    <td>{{ feeText(currentByMetal[m.key].sellFeeType, currentByMetal[m.key].sellFeeValue) }}</td>
                    <td class="text-right">
                      <v-btn size="x-small" variant="text" @click="copyCurrent(m.key)">불러오기</v-btn>
                    </td>
                  </template>
                  <template v-else>
                    <td colspan="4" class="text-grey">설정 전 — 고객가가 계산되지 않습니다</td>
                  </template>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 새 버전 -->
      <v-col cols="12" md="7">
        <v-card rounded="lg" elevation="0" border>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            새 버전 등록
            <span class="text-caption text-grey ml-2">고치지 않고 쌓습니다 — 앞 버전은 자동으로 닫힙니다</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row no-gutters class="mb-3">
              <v-col cols="12" sm="4" class="pr-sm-3 mb-3 mb-sm-0">
                <v-select
                  v-model="form.metal"
                  :items="METALS.map((m) => ({ title: m.label, value: m.key }))"
                  label="금속"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="4" class="pr-sm-3 mb-3 mb-sm-0">
                <v-text-field
                  v-model="form.roundUnit"
                  type="number"
                  min="1"
                  label="반올림 단위(원)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.effectiveFrom"
                  type="datetime-local"
                  label="적용 시작 (비우면 지금)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row v-for="row in [
              { key: 'base', label: '기본 수수료', hint: '양방향에 깔립니다' },
              { key: 'buy', label: '살 때', hint: '기본에 더해집니다' },
              { key: 'sell', label: '팔 때', hint: '기본에 더해집니다' },
            ]" :key="row.key" no-gutters align="center" class="mb-3">
              <v-col cols="12" sm="3" class="pr-sm-3">
                <div class="text-body-2 font-weight-medium">{{ row.label }}</div>
                <div class="text-caption text-grey">{{ row.hint }}</div>
              </v-col>
              <v-col cols="6" sm="4" class="pr-3">
                <v-select
                  v-model="form[row.key + 'FeeType']"
                  :items="FEE_TYPES"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </v-col>
              <v-col cols="6" sm="5">
                <v-text-field
                  v-model="form[row.key + 'FeeValue']"
                  type="number"
                  step="0.01"
                  min="0"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :suffix="form[row.key + 'FeeType'] === 'PCT' ? '%' : '원/g'"
                />
              </v-col>
            </v-row>

            <v-text-field
              v-model="form.memo"
              label="메모 — 왜 바꾸는지"
              density="compact"
              variant="outlined"
              hide-details
              class="mb-4"
            />

            <!-- 미리보기. 저장 전에 소수점 실수를 잡는 자리다. -->
            <!-- color prop 을 쓰지 않는다. 다크 스킨이 .v-main 안 카드 배경을
                 !important 로 덮어서 배경은 안 바뀌고, Vuetify 가 같이 넣는
                 어두운 글자색만 남아 숫자가 검게 떴다(2026-10-18). -->
            <v-card v-if="preview" flat rounded="lg" class="pa-4 mb-4">
              <div class="text-caption text-grey mb-2">
                기준 시세 {{ money(sampleQuote) }}원/g 일 때
                ({{ metalLabel(form.metal) }} · {{ sampleSource }})
              </div>
              <div class="d-flex flex-wrap">
                <div class="mr-8">
                  <div class="text-caption text-grey">고객이 살 때</div>
                  <div class="text-h6 font-weight-bold fee-buy">{{ money(preview.buy) }}원</div>
                </div>
                <div class="mr-8">
                  <div class="text-caption text-grey">고객이 팔 때</div>
                  <div class="text-h6 font-weight-bold fee-sell">{{ money(preview.sell) }}원</div>
                </div>
                <div>
                  <div class="text-caption text-grey">폭</div>
                  <div class="text-h6">{{ money(preview.spread) }}원</div>
                </div>
              </div>
              <!-- text-body-2 를 쓰지 않는다. 스킨이 그 클래스 색을 !important 로
                   덮어 text-red 를 이기므로, 빨간 경고가 회색으로 나온다. -->
              <div v-if="preview.broken" class="fee-warn mt-2">
                팔 때 가격이 0 이하입니다. 수수료가 시세를 넘었습니다 — 저장되지 않습니다.
              </div>
              <div class="text-caption text-grey mt-2">부가세 전 금액입니다.</div>
            </v-card>

            <div class="d-flex">
              <v-spacer />
              <v-btn
                color="amber-darken-2"
                variant="flat"
                :loading="saving"
                :disabled="preview && preview.broken"
                @click="save"
              >등록</v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 이력 -->
    <v-card rounded="lg" elevation="0" border class="mt-5">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        이력
        <span class="text-caption text-grey ml-2">지난 주문의 가격 근거라 지우지 않습니다</span>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <v-table density="comfortable">
          <thead>
            <tr>
              <th>금속</th>
              <th>기본</th>
              <th>살 때</th>
              <th>팔 때</th>
              <th>반올림</th>
              <th>적용 시작</th>
              <th>종료</th>
              <th>메모</th>
              <th class="text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && policies.length === 0">
              <td colspan="9" class="text-center text-grey py-6">등록된 수수료가 없습니다</td>
            </tr>
            <tr v-for="p in policies" :key="p.id" :class="{ 'text-grey': !p.active }">
              <td>{{ metalLabel(p.metal) }}</td>
              <td>{{ feeText(p.baseFeeType, p.baseFeeValue) }}</td>
              <td>{{ feeText(p.buyFeeType, p.buyFeeValue) }}</td>
              <td>{{ feeText(p.sellFeeType, p.sellFeeValue) }}</td>
              <td>{{ money(p.roundUnit) }}원</td>
              <td>{{ dateText(p.effectiveFrom) }}</td>
              <td>
                <v-chip v-if="scheduled(p)" size="x-small" color="blue" variant="tonal">예정</v-chip>
                <span v-else-if="p.effectiveTo">{{ dateText(p.effectiveTo) }}</span>
                <v-chip v-else size="x-small" color="green" variant="tonal">적용 중</v-chip>
              </td>
              <td class="text-caption">{{ p.memo || '-' }}</td>
              <td class="text-right">
                <v-btn
                  v-if="p.active"
                  size="x-small"
                  variant="text"
                  color="grey"
                  @click="deactivate(p)"
                >끄기</v-btn>
                <v-chip v-else size="x-small" variant="tonal">꺼짐</v-chip>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
  </div>
</template>
<style scoped>
/* 살 때(고객이 내는 값)와 팔 때(고객이 받는 값)를 한눈에 가른다. 다크 판 위라
   red-darken·blue-darken 계열은 어두워서 쓰지 않는다. */
.fee-buy {
  color: #ffb4a2;
}

.fee-sell {
  color: #9ecbff;
}

/* 경고는 빨갛게 보여야 한다. 다크 판 위라 red-darken 계열은 어두워서 안 쓴다. */
.fee-warn {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #ff8a80;
}
</style>
